import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getUser } from "~/api/account/requests";
import { AccountContext } from "~/contexts/Account";
import { IAccount } from "~/types/account";
import { Userpilot } from "userpilot";
import _debounce from "lodash/debounce";
import { useIntercom } from "react-use-intercom";
import { sentryProject, intercomKey } from "~/helpers/environment";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { useStore } from "~/hooks/useStore";
import { logger } from "~/helpers/logger";

export const useAccount = () => {
  const intercomBooted = useRef(false);
  const { account, setAccount } = useContext(AccountContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isInterCom, setIsInterCom] = useState(false);
  const { boot, show } = useIntercom();
  const { store } = useStore();

  const bootIntercomForAccount = (account: IAccount) => {
    if (!intercomKey) {
      setIsInterCom(false);
      return;
    }

    setIsInterCom(true);
    boot({
      email: account.email,
      userId: sentryProject + "_" + account.id.toString(),
      name: account.name,
      customAttributes: {
        app: sentryProject,
        shop_name: store?.name,
        shop_url: store?.url,
        shop_email: store?.email,
        shop_phone: store?.phone,
      },
    });
  };

  const bootIntercom = () => (account ? bootIntercomForAccount(account) : null);

  const showIntercom = () => {
    if (account) show();
  };

  const setAccountWithThirdParties = (acc: IAccount | null) => {
    setAccount(acc);

    if (acc) {
      // Set user context for Sentry logging
      logger.setUser({
        id: acc.id,
        email: acc.email,
        username: acc.name,
      });

      // Add additional context for Sentry
      logger.setContext("account", {
        payment_provider: acc.payment_provider,
        signup_origin: acc.signup_origin,
        created_at: acc.created_at,
        subscription_active: !!acc.active_subscription,
        plan_name: acc.active_subscription?.plan?.name,
      });

      Userpilot.identify(acc.id.toString(), {
        email: acc.email,
        created_at: acc.created_at,
        name: acc.name,
        payment_provider: acc.payment_provider,
        signup_origin: acc.signup_origin,
      });
      if (
        (acc.active_subscription || DISABLE_PAYMENTS) &&
        !intercomBooted.current
      ) {
        bootIntercomForAccount(acc);
      }
    } else {
      // Clear user context on logout
      logger.clearUser();
    }
  };

  const getAccount = async () => {
    setIsLoading(true);
    const profile = (await getUser()) as IAccount;
    setAccountWithThirdParties(profile);
    setIsLoading(false);
    return profile;
  };
  const debouncedGetAccount = useCallback(_debounce(getAccount, 200), []);

  useEffect(() => {
    if (!account) {
      debouncedGetAccount();
      setIsLoading(false);
    }
  }, []);

  const isPerpetual = () => {
    if (!account) return false;
    return account.last_subscription?.plan?.name === "Perpetual Unicorn Plan";
  };

  return {
    account,
    isInterCom,
    setAccount: setAccountWithThirdParties,
    getAccount,
    refetch: getAccount,
    isPerpetual: isPerpetual(),
    bootIntercom,
    isLoading,
    showIntercom,
  };
};
